import { useCallback } from 'react';
import { useAuthStore } from './useAuthStore';
import type { ExtendedAuthStore } from './useAuthStore';
import type { UserRole } from '../types/user';
import type { SubscriptionPlan } from '../types/organisation';

/**
 * Plan hierarchy for hasMinPlan() comparisons.
 * Higher index = higher tier.
 */
const PLAN_HIERARCHY: Record<SubscriptionPlan, number> = {
  STARTER: 0,
  GROWTH: 1,
  PROFESSIONAL: 2,
};

/**
 * useAuthPermissions, lightweight role and plan guards for the current
 * authenticated user.  Reads directly from the Zustand auth store; no
 * network calls.
 *
 * Usage:
 *   const { hasRole, hasMinPlan, isSuperAdmin } = useAuthPermissions();
 *
 * SUPER_ADMIN behaviour:
 *   - `isSuperAdmin` is true
 *   - `isAdmin` is also true (SUPER_ADMIN can do everything an ADMIN can)
 *   - `hasRole('SUPER_ADMIN')` is true; `hasRole('ADMIN')` is false unless
 *     you pass both: `hasRole(['SUPER_ADMIN', 'ADMIN'])`
 *   - `hasMinPlan` always returns true for SUPER_ADMIN (platform-level users
 *     are not constrained by any org's subscription plan)
 *
 * Org switching (SUPER_ADMIN only):
 *   - Call `scopeOrg(orgId)` to set the X-Organisation-Id header in all
 *     subsequent API requests.  Call `scopeOrg(null)` to return to global view.
 *   - The header is managed via the Zustand store's `scopedOrgId` field.
 */
export function useAuthPermissions() {
  const user = useAuthStore((state) => state.user);
  const scopedOrgId = useAuthStore((state) => (state as ExtendedAuthStore).scopedOrgId ?? null);
  const setScopedOrg = useAuthStore((state) => (state as ExtendedAuthStore).setScopedOrg);

  const role = user?.role as UserRole | undefined;

  /**
   * Returns true if the current user has at least one of the given roles.
   * Always returns false when unauthenticated.
   */
  const hasRole = useCallback(
    (required: UserRole | UserRole[]): boolean => {
      if (!role) return false;
      const roles = Array.isArray(required) ? required : [required];
      return roles.includes(role);
    },
    [role]
  );

  // isSuperAdmin is derived from isRootOrg (set by the JWT rootOrg claim).
  // Fall back to role check for tokens issued before the rootOrg claim existed.
  const isSuperAdmin = user?.isRootOrg === true || role === 'SUPER_ADMIN';

  /**
   * Returns true if the current user's organisation is on at least the
   * given plan tier.
   * Root-org users always pass, they are not bounded by any org plan.
   */
  const hasMinPlan = useCallback(
    (minPlan: SubscriptionPlan): boolean => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      if (!user.organisationPlan) return false;
      const currentLevel = PLAN_HIERARCHY[user.organisationPlan as SubscriptionPlan] ?? -1;
      const requiredLevel = PLAN_HIERARCHY[minPlan] ?? Infinity;
      return currentLevel >= requiredLevel;
    },
    [user, isSuperAdmin]
  );

  /**
   * Scope subsequent API calls to a specific org (root-org users only).
   * Pass null to return to the global (unscoped) view.
   */
  const scopeOrg = useCallback(
    (orgId: string | null) => {
      if (!isSuperAdmin) return;
      setScopedOrg?.(orgId);
    },
    [isSuperAdmin, setScopedOrg]
  );

  const isAdmin = isSuperAdmin || role === 'ADMIN';
  const isManager = isAdmin || hasRole(['AGENT', 'PROPERTY_MANAGER']);
  const isOwner = hasRole(['OWNER', 'LANDLORD']);
  const isVendor = hasRole('VENDOR');
  const isTenant = hasRole(['TENANT', 'CLIENT']);

  return {
    hasRole,
    hasMinPlan,
    scopeOrg,
    isSuperAdmin,
    isAdmin,
    isManager,
    isOwner,
    isVendor,
    isTenant,
    currentRole: role,
    currentPlan: user?.organisationPlan as SubscriptionPlan | undefined,
    organisationId: user?.organisationId ?? null,
    /** The org the SUPER_ADMIN is currently scoped to (null = global view) */
    scopedOrgId: isSuperAdmin ? scopedOrgId : null,
  };
}
